import React from 'react';
import SkeletonCommon from './SkeletonCommon';

interface SkeletonTableProps {
  columns?: number;
  rows?: number;
  hasHeader?: boolean;
  className?: string;
  hasCode?: boolean; // Code column
  hasName?: boolean; // Name column
  hasStatus?: boolean; // Status column
  hasActions?: boolean; // Actions column
  hasPagination?: boolean;
  columnWidths?: string[];
  hasFilters?: boolean;
}

const SkeletonTable: React.FC<SkeletonTableProps> = ({
  columns = 3,
  rows = 8,
  hasHeader = true,
  className = '',
  hasCode = true,
  hasName = true,
  hasStatus = true,
  hasActions = true,
  hasPagination = true,
  columnWidths = [],
  hasFilters = true,
}) => {
  const fixedColumns = (hasCode ? 1 : 0) + (hasName ? 1 : 0) + (hasStatus ? 1 : 0) + (hasActions ? 1 : 0);
  const middleColumns = Math.max(0, columns - fixedColumns + (hasCode ? 1 : 0) + (hasName ? 1 : 0));
  const totalColumns = middleColumns + (hasStatus ? 1 : 0) + (hasActions ? 1 : 0);

  const columnTypes: string[] = [];

  if (hasCode) columnTypes.push('code');
  if (hasName) columnTypes.push('name');

  for (let i = columnTypes.length; i < middleColumns; i++) {
    columnTypes.push('middle');
  }

  if (hasStatus) columnTypes.push('status');
  if (hasActions) columnTypes.push('actions');

  const getDefaultWidth = (type: string) => {
    switch (type) {
      case 'code':
        return '120px';
      case 'name':
        return '1fr';
      case 'middle':
        return '1fr';
      case 'status':
        return '260px';
      case 'actions':
        return '130px';
      default:
        return '1fr';
    }
  };

  const gridTemplateColumns =
    columnWidths.length === totalColumns
      ? columnWidths.join(' ')
      : columnTypes.map((type) => getDefaultWidth(type)).join(' ');

  return (
    <div className={`w-full overflow-hidden ${className}`}>
      {/* Table Container */}
      <div className='w-full bg-white'>
        {/* Table Header */}
        {hasHeader && (
          <div
            className='grid gap-4 px-[24px] py-[16px] border-y border-gray-200 bg-[#fafafa] items-center'
            style={{ gridTemplateColumns }}
          >
            {columnTypes.map((type, i) => {
              const isActionColumn = type === 'actions';
              return (
                <div
                  key={`header-${i}`}
                  className={`flex items-center ${isActionColumn ? 'justify-center' : 'justify-between'}`}
                >
                  <SkeletonCommon type='text' width={isActionColumn ? 'auto' : '2/3'} className='h-5' />

                  {hasFilters && !isActionColumn && (
                    <div className='flex items-center space-x-1'>
                      <SkeletonCommon type='badge' className='w-4 h-4' radius='sm' />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Table Body */}
        <div className='divide-y divide-gray-200'>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <div
              key={`row-${rowIndex}`}
              className='grid gap-4 px-[24px] py-[16px] items-center bg-white hover:bg-gray-50 transition-colors'
              style={{ gridTemplateColumns }}
            >
              {columnTypes.map((type, colIndex) => {
                if (type === 'actions') {
                  return (
                    <div key={`cell-${rowIndex}-${colIndex}`} className='flex items-center justify-end space-x-2'>
                      <SkeletonCommon type='badge' className='w-8 h-8' radius='full' />
                      <SkeletonCommon type='badge' className='w-8 h-8' radius='full' />
                      <SkeletonCommon type='badge' className='w-8 h-8' radius='full' />
                    </div>
                  );
                }

                if (type === 'status') {
                  return (
                    <div key={`cell-${rowIndex}-${colIndex}`} className='flex items-center'>
                      <div className='w-12 h-6 rounded-full relative overflow-hidden'>
                        <SkeletonCommon type='badge' className='w-12 h-6 absolute' radius='full' />
                        <div className='absolute w-5 h-5 rounded-full bg-white top-0.5 right-1'></div>
                      </div>

                      <div className='flex items-center ml-2'>
                        <SkeletonCommon type='text' className='h-5 w-[80px]' radius='sm' />
                      </div>
                    </div>
                  );
                }

                if (type === 'code') {
                  return (
                    <SkeletonCommon key={`cell-${rowIndex}-${colIndex}`} type='text' width='2/3' className='h-5' />
                  );
                }

                if (type === 'name') {
                  return (
                    <SkeletonCommon key={`cell-${rowIndex}-${colIndex}`} type='text' width='full' className='h-5' />
                  );
                }

                return <SkeletonCommon key={`cell-${rowIndex}-${colIndex}`} type='text' width='2/3' className='h-5' />;
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Pagination */}
      {hasPagination && (
        <div className='flex justify-between items-center px-[24px] py-[12px] bg-white border-t border-gray-200'>
          <div className='flex items-center text-gray-500'>
            <SkeletonCommon type='text' width='auto' className='w-[180px] h-5' />
          </div>
          <div className='flex items-center'>
            <div className='flex items-center space-x-1 mr-4'>
              <SkeletonCommon type='button' className='w-8 h-8' />
              <SkeletonCommon type='button' className='w-8 h-8' />
              <SkeletonCommon type='button' className='w-8 h-8 bg-blue-500' />
              <SkeletonCommon type='button' className='w-8 h-8' />
              <SkeletonCommon type='button' className='w-8 h-8' />
            </div>
          </div>
          <div className='flex items-center space-x-2'>
            <SkeletonCommon type='text' width='auto' className='w-[180px] h-5' />
            <SkeletonCommon type='badge' className='w-[60px] h-8' radius='md' />
          </div>
        </div>
      )}
    </div>
  );
};

export default SkeletonTable;
